import React from 'react';
import unitedStates from 'united-states';
import sourceTypes from '../../../source-types';
import transactionTypes from '../../../transaction-types';
import markets from '../../../markets';
import propertyCategories from '../../../property-categories';
import propertySubCategories from '../../../property-sub-categories';
import propertyTypes from '../../../property-types';
import singleTenantSubcategories from '../../../single-tenant-subcategories';
import multiTenantSubcategories from '../../../multi-tenant-subcategories';
import singleMultiTenantIndustrialSubcategories from '../../../single-multi-tenant-industrial-subcategories';
import singleTenantOffice from '../../../single-tenant-office-subcategories';
import { NeedOwnerToggle } from './OwnerChoice';
import OwnerList from './OwnerList';
import multiTenantOffice from '../../../multi-tenant-office-subcategories';
import { formatNumber } from '../../../utils';

// Options come from the same lists the property CRUD builds its dropdowns
// from, so a property added here can only hold values the rest of iCLIMB
// already understands. Free text in MARKET or CATEGORY made rows the property
// page could not then edit without correcting them first.

const STATE_OPTIONS = unitedStates
    .filter((state) => !state.isTerritory)
    .map((state) => ({ value: state.abbr, label: state.name }));

const SOURCE_OPTIONS = sourceTypes.map((source) => ({ value: source.value, label: source.label }));

const TRANSACTION_OPTIONS = transactionTypes.map((type) => ({ value: type, label: type }));

const MARKET_OPTIONS = markets.map((market) => ({ value: market.code, label: market.name }));

const CATEGORY_OPTIONS = propertyCategories.map((category) => ({ value: category.code, label: category.name }));

const SUB_CATEGORY_OPTIONS = propertySubCategories.map((sub) => ({ value: sub.code, label: sub.name }));

const TYPE_OPTIONS = propertyTypes.map((type) => ({ value: type.code, label: type.name }));

const plain = (values) => values.map((value) => ({ value, label: value }));

/**
 * Tenant subcategory, exactly as the property CRUD picks it: the list depends
 * on the category, and single- and multi-tenant write to different columns.
 */
function tenantSubcategory(values) {
    if (values.CATEGORY === 'apartments') return null;

    if (values.TYPE === 'STNL') {
        return {
            key: 'SINGLE_TENANT_SUBCATEGORY',
            label: 'Tenant Subcategory',
            options: plain(
                values.CATEGORY === 'office' ? singleTenantOffice
                    : values.CATEGORY === 'industrial' ? singleMultiTenantIndustrialSubcategories
                        : singleTenantSubcategories
            ),
        };
    }

    if (values.TYPE === 'MT') {
        return {
            key: 'MULTI_TENANT_SUBCATEGORY',
            label: 'Tenant Subcategory',
            options: plain(
                values.CATEGORY === 'office' ? multiTenantOffice
                    : values.CATEGORY === 'industrial' ? singleMultiTenantIndustrialSubcategories
                        : multiTenantSubcategories
            ),
        };
    }

    return null;
}

/**
 * Category, then type or sub category, then tenant subcategory — the chain the
 * property page shows, shared with the map's category filter.
 */
export function categoryFields(values) {
    return [
        { key: 'CATEGORY', label: 'Property Category', options: CATEGORY_OPTIONS },
        // Apartments carry a sub category and no property type; every
        // other category is the other way round.
        values.CATEGORY === 'apartments'
            ? { key: 'SUB_CATEGORY', label: 'Sub Category', options: SUB_CATEGORY_OPTIONS }
            : { key: 'TYPE', label: 'Property Type', options: TYPE_OPTIONS },
        tenantSubcategory(values),
    ].filter(Boolean);
}

/**
 * The design's three groups, against the PROPERTIES columns the backend
 * derives. Anything not here still travels with the parcel — this is the set
 * worth a look before saving, not the whole record.
 *
 * Labels and field types match the property CRUD one for one.
 */
export function formGroups(values) {
    return [
        {
            title: 'Identity',
            fields: [
                { key: 'NAME', label: 'Property Name', wide: true },
                { key: 'ADDRESS', label: 'Address', wide: true },
                { key: 'CITY', label: 'City' },
                { key: 'STATE', label: 'State', options: STATE_OPTIONS },
                { key: 'ZIP', label: 'Zip' },
                { key: 'COUNTY', label: 'County' },
            ],
        },
        {
            title: 'Classification',
            fields: [
                { key: 'MARKET', label: 'Market', options: MARKET_OPTIONS },
                { key: 'SOURCE', label: 'Source', options: SOURCE_OPTIONS },
                { key: 'TRANSACTION_TYPE', label: 'Transaction Type', options: TRANSACTION_OPTIONS },
                ...categoryFields(values),
            ],
        },
        {
            title: 'From LightBox',
            fields: [
                { key: 'YEAR_BUILT', label: 'Year Built' },
                { key: 'SQFT', label: 'Square Feet', format: 'commas' },
                { key: 'ACRES', label: 'Acres', format: 'decimal' },
                { key: 'ASSESSED_VALUE', label: 'Assessed Value', format: 'commas' },
            ],
        },
    ];
}

/**
 * The columns a change makes unreachable, so the parent knows what to clear.
 *
 * Category used to take Property Type with it, which is what the client hit:
 * "once I did that it kicked out Single Tenant in Property Types". Single- and
 * Multi-Tenant mean the same thing under every category, so only the move to
 * or from apartments — the one category with a sub category and no type —
 * makes either of those unreachable. The tenant subcategories go on any
 * category change because their option lists really do differ: General Office
 * is not on the retail list.
 */
export function fieldsClearedBy(key, value) {
    const tenant = ['SINGLE_TENANT_SUBCATEGORY', 'MULTI_TENANT_SUBCATEGORY'];

    if (key === 'TYPE') return tenant;
    if (key !== 'CATEGORY') return [];

    return (value === 'apartments' ? ['TYPE'] : ['SUB_CATEGORY']).concat(tenant);
}

/** The same masks the property CRUD applies as you type. */
function mask(format, raw) {
    if (format === 'commas') return formatNumber(raw, true);
    if (format === 'decimal') return formatNumber(raw, false);

    return raw;
}

function Field({ field, value, onChange }) {
    const raw = value === null || value === undefined ? '' : String(value);
    // Masked on the way in as well as on change, so the parcel's own figures
    // arrive already formatted rather than only once they are touched.
    const shown = field.options ? raw : mask(field.format, raw);

    return (
        <label className={'Form-field' + (field.wide ? ' Form-field--wide' : '')}>
            <span className="Form-label">{field.label}</span>
            {field.options ? (
                <select
                    className="Form-input Form-select"
                    name={field.key}
                    value={shown}
                    onChange={(event) => onChange(field.key, event.target.value)}
                >
                    <option value="" />
                    {field.options.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                </select>
            ) : (
                <input
                    className="Form-input"
                    name={field.key}
                    value={shown}
                    onChange={(event) => onChange(field.key, mask(field.format, event.target.value))}
                />
            )}
        </label>
    );
}

/**
 * Add to iCLIMB, with the derived values shown first.
 *
 * The same card becomes the form rather than a second panel opening beside it,
 * so the parcel you were looking at stays the thing you are working on.
 */
export default function AddFormStep({
    values,
    isSaving,
    onChange,
    onSave,
    onCancel,
    parcel,
    candidates,
    owner,
    onOwnerChange,
    foundInvestors,
    onFoundInvestors,
    needOwner,
    onNeedOwnerChange,
    isCheckingKore,
}) {
    return (
        <div className="SidePanel-step">
            <div className="SidePanel-body">
                {formGroups(values).map((group) => (
                    <div key={group.title} className="Form-group">
                        <div className="Form-group-title">{group.title}</div>
                        <div className="Form-grid">
                            {group.fields.map((field) => (
                                <Field
                                    key={field.key}
                                    field={field}
                                    value={values[field.key]}
                                    onChange={onChange}
                                />
                            ))}
                        </div>
                    </div>
                ))}

                {/* Below From LightBox, where the client asked for it: the
                    same choice the card offers, so leaving the card does not
                    hide what this add is about to file the owner as. */}
                <div className="Form-group">
                    <div className="Form-group-title">
                        <span>Investor</span>
                        <NeedOwnerToggle value={Boolean(needOwner)} onChange={onNeedOwnerChange} />
                    </div>
                    {!needOwner && (
                        <OwnerList
                            parcel={parcel}
                            owner={owner}
                            onChange={onOwnerChange}
                            candidates={candidates}
                            found={foundInvestors}
                            onFound={onFoundInvestors}
                        />
                    )}
                </div>
            </div>

            <div className="SidePanel-footer">
                <button type="button" className="Btn Btn--grow Btn--primary" onClick={onSave} disabled={isSaving || isCheckingKore}>
                    {isCheckingKore ? 'Checking Kore…' : isSaving ? 'Saving…' : 'Add property'}
                </button>
                <button type="button" className="Btn" onClick={onCancel}>Cancel</button>
            </div>
        </div>
    );
}
